/**
 * Utility functions for exporting transaction data to CSV
 */

/**
 * Convert transaction data to CSV format
 * @param {Array} transactions - Array of transaction objects
 * @param {Array} categories - Array of category objects for mapping category IDs to names
 * @returns {string} CSV formatted string
 */
export const convertTransactionsToCSV = (transactions, categories = []) => {
  // Create category lookup map
  const categoryMap = new Map();
  categories.forEach(cat => {
    categoryMap.set(cat.id, cat.name);
  });

  // Define CSV headers
  const headers = [
    'Date',
    'Description',
    'Amount',
    'Account',
    'Category',
    'Balance',
    'Type',
    'Notes',
    'ID'
  ];

  // Convert transactions to CSV rows
  const rows = transactions.map(transaction => [
    transaction.date ? new Date(transaction.date).toISOString().split('T')[0] : '',
    `"${(transaction.description || '').replace(/"/g, '""')}"`, // Escape quotes in description
    transaction.amount || '',
    `"${(transaction.account || '').replace(/"/g, '""')}"`, // Escape quotes in account
    categoryMap.get(transaction.category) || 'Uncategorized',
    transaction.balance || '',
    `"${(transaction.type || '').replace(/"/g, '""')}"`, // Escape quotes in type
    `"${(transaction.notes || '').replace(/"/g, '""')}"`, // Escape quotes in notes
    transaction._id || transaction.id || ''
  ]);

  // Combine headers and rows
  const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  
  return csvContent;
};

/**
 * Download CSV data as a file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file to download
 */
export const downloadCSV = (csvContent, filename = 'transactions.csv') => {
  // Create blob with CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export filtered transactions to CSV
 * @param {Array} transactions - Array of transaction objects to export
 * @param {Array} categories - Array of category objects
 * @param {Object} filters - Current filter state for filename
 */
export const exportTransactionsToCSV = (transactions, categories, filters = {}) => {
  // Convert to CSV
  const csvContent = convertTransactionsToCSV(transactions, categories);
  
  // Generate filename with date range if filters are applied
  let filename = 'transactions';
  
  if (filters.startDate || filters.endDate) {
    const startDate = filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : 'all';
    const endDate = filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : 'all';
    filename += `_${startDate}_to_${endDate}`;
  }
  
  if (filters.category) {
    const categoryName = categories.find(cat => cat.id === filters.category)?.name || filters.category;
    filename += `_${categoryName.replace(/[^a-zA-Z0-9]/g, '_')}`;
  }
  
  filename += '.csv';
  
  // Download the file
  downloadCSV(csvContent, filename);
}; 